import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { loggedIn } from "../../redux/AuthSlice";
import Button from "../button/Button";

const VendorSignupModal = (props) => {
  const dispatch = useDispatch();
  const auth = useSelector((data) => data.auth);
  
  // Pre-fill data if vendor is reapplying (rejected status)
  const isReapplying = auth?.role === "vendor" && auth?.userDetail?.status === "rejected";
  
  const [name, setName] = useState(isReapplying ? auth.userDetail.name || "" : "");
  const [vendorPersonName, setVendorPersonName] = useState(isReapplying ? auth.userDetail.vendor_person_name || "" : "");
  const [phone, setPhone] = useState(isReapplying ? auth.userDetail.phone_no?.replace('+', '') || "" : "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [address, setAddress] = useState(isReapplying ? auth.userDetail.address || "" : "");
  const [address2, setAddress2] = useState(isReapplying ? auth.userDetail.address2 || "" : "");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Normalize phone number to Pakistan format (92XXXXXXXXXX)
  const normalizePhoneNumber = (value) => {
    // Remove all non-numeric characters
    let cleanValue = value.replace(/\D/g, "");

    // Handle different input formats
    if (cleanValue.startsWith("920")) {
      // User typed 03... which became 9203...
      cleanValue = "92" + cleanValue.slice(3);
    } else if (cleanValue.startsWith("03")) {
      // User typed 03... (missing country code)
      cleanValue = "92" + cleanValue.slice(1);
    } else if (cleanValue.startsWith("3")) {
      // User typed 3... (missing 92)
      cleanValue = "92" + cleanValue;
    } else if (!cleanValue.startsWith("92")) {
      // Any other format, prepend 92
      cleanValue = "92" + cleanValue;
    }

    // Limit to 12 digits (92 + 10 digits)
    if (cleanValue.length > 12) {
      cleanValue = cleanValue.substring(0, 12);
    }

    return cleanValue;
  };

  const handlePhoneChange = (value) => {
    const normalized = normalizePhoneNumber(value);
    setPhone(normalized);
  };

  const handleSignup = async () => {
    // Validation
    if (!name.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!vendorPersonName.trim()) {
      toast.error("Contact person name is required");
      return;
    }
    if (!phone || phone.length !== 12) {
      toast.error("Please enter a valid phone number (10 digits)");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the request body
      const requestBody = {
  name: name.trim(),
  vendor_person_name: vendorPersonName.trim(),
  phone_no: phone.startsWith("+") ? phone : `+${phone}`,
  password: password,
  address: address.trim(),
  address2: address2.trim()
};

      console.log("Sending vendor registration request:", {
        ...requestBody,
        password: "***hidden***"
      });

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}register-vendor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      console.log("Vendor registration response:", data);

      if (data.status) {
        // Auto-login the vendor after successful signup
        try {
          const loginResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL}login-vendor`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                phone_no: phone.startsWith("+") ? phone : `+${phone}`,
                password: password,
              }),
            }
          );

          const loginData = await loginResponse.json();

          if (loginData.status) {
            const userData = {
              token: loginData?.access_token,
              user: {
                ...loginData?.vendor,
                user_id: loginData?.user_id || data.user_id  // Store user_id separately
              },
              role: "vendor",
            };
            dispatch(loggedIn(userData));
            
            // Show single combined success message
            toast.success(
              "Application submitted successfully! You are now logged in. Approval may take up to 24 hours.",
              { autoClose: 5000 }
            );
          } else {
            // If auto-login fails, just show signup success
            toast.success(
              "Application submitted successfully! Please login to continue. Approval may take up to 24 hours."
            );
          }
        } catch (loginError) {
          console.error("Auto-login error:", loginError);
          // If auto-login fails, just show signup success
          toast.success(
            "Application submitted successfully! Please login to continue. Approval may take up to 24 hours."
          );
        }
        
        props.handleCloseVendorSignupModal();
        // Reset form
        setName("");
        setVendorPersonName("");
        setPhone("");
        setPassword("");
        setConfirmPassword("");
        setAddress("");
        setAddress2("");
      } else {
        // Show validation errors if present
        if (data.errors && data.errors.length > 0) {
          data.errors.forEach(error => {
            toast.error(error.msg);
          });
        } else {
          toast.error(data.message || "Failed to submit application");
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      size="md"
      show={true}
      onHide={props.handleCloseVendorSignupModal}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isReapplying ? "Reapply for Vendor Account" : "Vendor Registration"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow m-2 lgModals customScroll pt-0 mt-0">
        {isReapplying && (
          <div className="alert alert-warning mt-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Reapplying:</strong> Your previous application was rejected. 
            Please update your details and submit again.
          </div>
        )}
        <div className="row">
          <div className="col-12 mt-3">
            <label className="form-label">Business Name *</label>
            <input
              type="text"
              className="form-control"
              style={{ padding: "8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
              placeholder="Enter business name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Contact Person Name *</label>
            <input
              type="text"
              className="form-control"
              style={{ padding: "8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
              placeholder="Enter contact person name"
              value={vendorPersonName}
              onChange={(e) => setVendorPersonName(e.target.value)}
            />
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Phone Number *</label>
            <PhoneInput
              country={"pk"}
              value={phone}
              onChange={handlePhoneChange}
              inputStyle={{ width: "100%", height: "38px", borderRadius: "6px", fontSize: "14px" }}
              onlyCountries={['pk']}
              disableDropdown={true}
              placeholder="+92 xxx xxxxxxx"
            />
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Password *</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                style={{ padding: "8px 50px 8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
                placeholder="Enter password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                  color: "#666",
                  fontSize: "12px",
                  fontWeight: "500",
                  textDecoration: "underline"
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Confirm Password *</label>
            <div style={{ position: "relative" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control"
                style={{ padding: "8px 50px 8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                  color: "#666",
                  fontSize: "12px",
                  fontWeight: "500",
                  textDecoration: "underline"
                }}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Address Line 1 *</label>
            <input
              type="text"
              className="form-control"
              style={{ padding: "8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
              placeholder="Enter address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="col-12 mt-3">
            <label className="form-label">Address Line 2</label>
            <input
              type="text"
              className="form-control"
              style={{ padding: "8px 12px", height: "38px", borderRadius: "6px", fontSize: "14px" }}
              placeholder="Enter additional address (optional)"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
            />
          </div>

          <div className="col-12 mt-4">
            <div className="alert alert-info">
              <small>
                <i className="fas fa-info-circle me-2"></i>
                Your application will be reviewed by our admin team. You will be
                able to login once approved. This process may take up to 24
                hours.
              </small>
            </div>
          </div>

          <div className="col-12 mt-3">
            <Button
              label={isLoading ? "Submitting..." : (isReapplying ? "Resubmit Application" : "Submit Application")}
              className="greenBtn w-100"
              onClick={handleSignup}
              loading={isLoading}
              disabled={isLoading}
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VendorSignupModal;
